import {EmbedCodeExtractor} from "./types"

/**
 * YouTube embed-code extractor (resolve-long-embed-codes, design D3). Sample:
 *
 *   <iframe width="560" height="315" src="https://www.youtube.com/embed/SMTKq-BImzo"
 *     title="YouTube video player" frameborder="0" allow="…" allowfullscreen></iframe>
 *
 * Emits the canonical watch URL, which the YouTube provider's link pattern matches
 * (providers/youTube.ts — its pattern deliberately refuses to match ids inside HTML
 * attributes, so the bare watch URL is required). Server-side this is an INTENTIONAL
 * behavior change: single YouTube iframes were previously intercepted by the
 * generic-iframe pre-processor and rendered as sandboxed generic iframes; registered
 * before genericIframe (specific-before-generic), they now resolve via the YouTube
 * provider — identical to pasting the watch URL.
 */
// (?![\w-]) — an id longer than 11 chars must fall through (raw submission → server),
// not be truncated to the first 11 chars of an unrelated-but-valid-looking video id
// (Copilot finding on PR #789). The full src is captured to inspect its query string.
// youtube-nocookie.com is deliberately NOT matched: rewriting a privacy-enhanced embed
// to a plain watch URL would silently drop no-cookie mode — those iframes keep the
// generic-iframe path with their exact src, like the query-option carve-out.
const YOUTUBE_IFRAME_PATTERN = /<iframe[^>]*\ssrc=["']((?:https?:)?\/\/(?:www\.)?youtube\.com\/embed\/([\w-]{11})(?![\w-])[^"']*)["']/i

export const youTubePreProcessor: EmbedCodeExtractor = (input: string) => {
  const match = YOUTUBE_IFRAME_PATTERN.exec(input)
  if(match === null) return null
  // Embed-only query options (?start=, ?list=, …) cannot be represented on the
  // canonical watch URL — the YouTube provider resolves from `watch?v=<id>` alone, so
  // reducing would silently drop them (a timestamped embed would play from 0:00).
  // Fall through instead: the exact iframe is preserved via the generic-iframe path,
  // exactly as before this change (Codex P2 on PR #789).
  //
  // EXCEPTION: the `si` share-tracking parameter. YouTube's share dialog appends
  // `?si=<token>` to every copied embed code, so treating it as meaningful would
  // exclude virtually all real-world pastes from the provider upgrade. It has no
  // playback semantics — ignore it. (`&amp;` first: the src is an HTML attribute
  // value, so ampersands arrive entity-escaped.)
  const queryIndex = match[1].indexOf("?")
  if(queryIndex !== -1) {
    const query = match[1].slice(queryIndex + 1).replaceAll("&amp;", "&")
    const meaningful = query.split("&").filter(param => param !== "" && !param.startsWith("si="))
    if(meaningful.length > 0) return null
  }
  return `https://www.youtube.com/watch?v=${match[2]}`
}
